/**
 * Alerts settings — the alert rule config + recipients, promoted to their OWN
 * top-level Settings section (one click) rather than buried under System
 * Preferences → a tab (Eric: "the alerts settings shouldn't be nested in multiple
 * layers"). Same panels, un-nested. Superadmin-only, matching where they lived.
 */
import { useCallback, useState } from 'react';
import { Siren } from 'lucide-react';
import { useSettingsContext } from '@/contexts/SettingsContext';
import { SystemAlertsPanel } from '@/components/settings/SystemAlertsPanel';
import { AlertRecipientsPanel } from '@/components/settings/AlertRecipientsPanel';
import { AlertSummaryPanel } from '@/components/settings/AlertSummaryPanel';
import { EmailRoutesPanel } from '@/components/settings/EmailRoutesPanel';

export function AlertsSettings() {
  const { is_superadmin, loading } = useSettingsContext();
  const [summaryKey, setSummaryKey] = useState(0);
  const refreshSummary = useCallback(() => setSummaryKey((n) => n + 1), []);

  if (loading) return null;
  if (!is_superadmin) {
    return (
      <div className="p-6 text-sm text-muted-foreground">
        These settings are available to superadmins.
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto p-4 md:p-6">
      <div className="mb-4 flex items-center gap-2">
        <Siren className="h-5 w-5 text-[#8FB560]" />
        <h2 className="text-lg font-semibold text-foreground">Alerts &amp; Reports</h2>
      </div>
      <p className="mb-6 max-w-2xl text-sm text-muted-foreground">
        Configure which fleet-health and telematics alerts fire and who receives them,
        and control every kind of outbound email the platform sends.
      </p>
      <SystemAlertsPanel />
      <AlertRecipientsPanel onChanged={refreshSummary} />
      {/* Re-read when the recipients or the email switch below change, so its status is current. */}
      <AlertSummaryPanel refreshKey={summaryKey} />
      <div className="mt-6">
        <EmailRoutesPanel onChanged={refreshSummary} />
      </div>
    </div>
  );
}
